/* eslint-disable func-names */
import * as Yup from 'yup';
import validator from 'common-validator-fif-payments';

import {
  isStringWithSpacesWithoutSpecialCharactersAndNumbers,
  isNumber,
  isIdentificationDocument,
} from '@helpers/validator';
import { getEnv } from '@config';
import { getRegionNumber, reverseFullAmount } from '@helpers';
import validationsWebhooks from './validationsWebhooks';

const { country } = getEnv();

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 20;

Yup.addMethod(Yup.string, 'validIdByCountry', function (errorMessage) {
  return this.test(`test-card-length`, errorMessage, function (value, context) {
    const { path, createError } = this;
    const docType = context.parent.doc_type;
    return isIdentificationDocument(value, docType) || createError({ path, message: errorMessage });
  });
});

Yup.addMethod(Yup.string, 'isName', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value) {
    const { path, createError } = this;
    return (
      validator.isName(value, MAX_NAME_LENGTH, MIN_NAME_LENGTH) ||
      createError({ path, message: errorMessage })
    );
  });
});

Yup.addMethod(Yup.string, 'isPhoneForCountry', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value) {
    const { path, createError } = this;
    if (validator.isPhoneForCountry(country, `${getRegionNumber(country)}${value}`)) return true;
    throw createError({ path, message: errorMessage });
  });
});

Yup.addMethod(Yup.string, 'onlyLetters', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value) {
    const { path, createError } = this;
    return (
      isStringWithSpacesWithoutSpecialCharactersAndNumbers(value) ||
      createError({ path, message: errorMessage })
    );
  });
});

Yup.addMethod(Yup.string, 'isNumber', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value) {
    const { path, createError } = this;
    return isNumber(value) || createError({ path, message: errorMessage });
  });
});

Yup.addMethod(Yup.string, 'isValidRefundAmount', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value = 0) {
    const reverAmount = reverseFullAmount(value);
    const { path, createError, from } = this;
    return (
      reverAmount <= from[0].value.max_refund_amount || createError({ path, message: errorMessage })
    );
  });
});

Yup.addMethod(Yup.string, 'isLessThanOriginalTransaction', function (errorMessage) {
  return this.test(`test-name`, errorMessage, function (value = 0) {
    const reverAmount = reverseFullAmount(value);
    const { path, createError, from } = this;
    return (
      reverAmount <= from[0].value.original_transaction_amount ||
      createError({ path, message: errorMessage })
    );
  });
});

export const validations = {
  email: Yup.string()
    .required('errors.common.required')
    .matches(
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'errors.common.invalidEmail'
    ),
  password: Yup.string()
    .required('errors.password.required')
    .matches(/^[0-9]+$/, 'errors.onlyNumbers')
    .min(6, 'errors.password.length')
    .max(6, 'errors.password.length'),
  repeatPassword: Yup.string()
    .required('errors.password.required')
    .matches(/^[0-9]+$/, 'errors.onlyNumbers')
    .min(6, 'errors.password.length')
    .max(6, 'errors.password.length')
    .oneOf([Yup.ref('password'), null], 'errors.password.errorPassword'),
  entryPassword: Yup.string().required('errors.password.required'),
  merchant_name: Yup.string()
    .required('errors.name.required')
    .matches(/^[^<>]+$/, 'errors.name.notMoreOrLess')
    .min(2, 'errors.password.length')
    .max(100, 'errors.password.length'),
  doc_type: Yup.string(),
  doc_number: Yup.string()
    .required('errors.docNumber.required')
    .validIdByCountry('errors.docNumber.invalid'),
  first_name: Yup.string()
    .required('errors.name.required')
    .onlyLetters('errors.onlyLetters')
    .min(MIN_NAME_LENGTH, 'errors.name.length')
    .max(MAX_NAME_LENGTH, 'errors.name.length')
    .isName('errors.name.valid'),
  last_name: Yup.string()
    .required('errors.lastName.required')
    .onlyLetters('errors.onlyLetters')
    .min(MIN_NAME_LENGTH, 'errors.name.length')
    .max(MAX_NAME_LENGTH, 'errors.name.length')
    .isName('errors.lastName.valid'),
  patern_last: Yup.string()
    .required('errors.lastName.required')
    .onlyLetters('errors.onlyLetters')
    .min(MIN_NAME_LENGTH, 'errors.name.length')
    .max(MAX_NAME_LENGTH, 'errors.name.length')
    .isName('errors.lastName.valid'),
  matern_last: Yup.string()
    .notRequired()
    .test('matern_last', (value) => {
      if (value) {
        const innerSchema = Yup.object().shape({
          matern_last: Yup.string()
            .onlyLetters('errors.onlyLetters')
            .min(MIN_NAME_LENGTH, 'errors.name.length')
            .max(MAX_NAME_LENGTH, 'errors.name.length')
            .isName('errors.lastName.valid'),
        });
        return innerSchema.validateSync({ matern_last: value });
      }
      return true;
    }),
  phone_number: Yup.string()
    .required('errors.phone.required')
    .isNumber('errors.phone.onlyNumbers')
    .isPhoneForCountry('errors.phone.valid'),
  refund_amount: Yup.string()
    .isLessThanOriginalTransaction('transactions.refunds.errorLessThanSaleAmount')
    .isValidRefundAmount('transactions.refunds.errorSaleRefundAmount'),
  ...validationsWebhooks,
};

const schema = (keys) => {
  const validators = keys.reduce((res, current) => {
    res[current] = validations[current];
    return res;
  }, {});
  return Yup.object().shape(validators);
};

// returns object field - error message
// example: {user_name: "errors.password.length", user_lastname: "errors.rut.invalid"}
export const getYupValidationErrors = (validationErrors, translator) => {
  if (!validationErrors.inner) return {};
  return validationErrors.inner?.reduce((errors, currentValidation) => {
    if (errors[currentValidation.path]) return errors;
    return Object.assign(errors, {
      [currentValidation.path]: translator
        ? translator(currentValidation.errors[0])
        : currentValidation.errors[0],
    });
  }, {});
};

export default schema;
